import React, { useEffect, useState, useRef } from 'react';
import Swal from 'sweetalert2';
import { collection, doc, getDoc, onSnapshot, orderBy, query, Timestamp, updateDoc, where } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import "../../App.css";
import Menu from '../../panel/Menu';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import numberToWords from "number-to-words";
import { getAuth } from "firebase/auth";
import {
    Box,
    Grid,
    CardContent,
    Typography,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Paper,
    Button
} from "@mui/material";

function Hotelinvoice() {
    const Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        didOpen: (toast) => {
            toast.addEventListener('mouseenter', Swal.stopTimer);
            toast.addEventListener('mouseleave', Swal.resumeTimer);
        }
    });

    const { documentId } = useParams();
    const [userData, setUserData] = useState(null);
    const [loadingCount, setLoadingCount] = useState(true);
    const [propData, setPropData] = useState({});
    const [bookingData, setBookingData] = useState(null);

    // Editable Customer States
    const [custName, setCustName] = useState("");
    const [mobile, setMobile] = useState("");
    const [email, setEmail] = useState("");
    const [address, setAddress] = useState("");
    const [city, setCity] = useState("");
    const [state, setState] = useState("");
    const [gender, setGender] = useState("Male");
    const [visitPurpose, setVisitPurpose] = useState("Darshan");
    const [gstName, setGstName] = useState("");
    const [gstNo, setGstNo] = useState("");
    const [bookingSource, setBookingSource] = useState("Walking");
    const [documentType, setDocumentType] = useState("");
    const [idNo, setIdNo] = useState("");
    const [adults, setAdults] = useState("");
    const [childs, setChilds] = useState("");

    // Calculation States
    const [subtotal, setSubtotal] = useState("0.00");
    const [cgst, setCgst] = useState("0.00");
    const [sgst, setSgst] = useState("0.00");
    const [taxes, setTaxes] = useState("0.00");
    const [payHistory, setPayHistory] = useState([]);
    const [amountInWords, setAmountInWords] = useState("");

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            setUserData(user);
            if (user) fetchUsername(user.uid);
        });
        return () => unsubscribe();
    }, []);

    const fetchUsername = async (uid) => {
        try {
            if (loadingCount) {
                const docRef = doc(db, "users", uid);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setLoadingCount(false);
                }
            }
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        const loadPropertyAndBooking = async () => {
            const currentUser = getAuth().currentUser;
            if (!currentUser) return;

            const roleSnap = await getDoc(doc(db, "authroles", currentUser.uid));
            if (!roleSnap.exists()) return;

            const pid = roleSnap.data().propertyId;
            const propSnap = await doc(db, "properties", pid);
            const propDoc = await getDoc(propSnap);
            if (propDoc.exists()) {
                setPropData(propDoc.data());
            }

            // Fetch Reservation Details
            if (documentId) {
                const docRef = doc(db, "reservations", documentId);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setBookingData(data);

                    const totalAmount = parseFloat(data.totalamount || 0);
                    const roomTotal = parseFloat(data.rooamount || 0) * parseFloat(data.noofdays || 1);

                    // Standard Tax Breakup Calculations (e.g. 5% or adaptable split)
                    let taxAmount = totalAmount * 5 / 105;
                    let taxableAmt = roomTotal - taxAmount;
                    let halfTax = (taxAmount / 2).toFixed(2);

                    setSubtotal(taxableAmt.toFixed(2));
                    setTaxes(taxAmount.toFixed(2));
                    setCgst(halfTax);
                    setSgst(halfTax);

                    if (data.totalamount) {
                        setAmountInWords(numberToWords.toWords(Math.round(data.totalamount)));
                    }
                }

                // Payment History Listener
                const q1 = query(collection(db, 'payhistory'), orderBy("createdAt", "asc"), where('bookingid', '==', documentId));
                onSnapshot(q1, async (querySnapshot1) => {
                    const combinedData = [];
                    for (const doc1 of querySnapshot1.docs) {
                        const payData = doc1.data();
                        let paymode1 = "";
                        let paymode2 = "";

                        if (payData.paymode1) {
                            const p1Snap = await getDoc(doc(db, "paymentmodes", payData.paymode1));
                            if (p1Snap.exists()) paymode1 = p1Snap.data().paymode;
                        }
                        if (payData.paymode2) {
                            const p2Snap = await getDoc(doc(db, "paymentmodes", payData.paymode2));
                            if (p2Snap.exists()) paymode2 = p2Snap.data().paymode;
                        }

                        const date = payData.createdAt?.toDate ? payData.createdAt.toDate() : new Date();
                        const createdat = moment(date).format("DD-MM-YYYY HH:mm:ss");

                        combinedData.push({
                            id: doc1.id,
                            data: payData,
                            paymode1,
                            paymode2,
                            createdat,
                        });
                    }
                    setPayHistory(combinedData);
                });
            }
        };

        loadPropertyAndBooking();
    }, [documentId]);

    const getCustData = () => {
        if (!bookingData || !bookingData.customers) return;
        const c = bookingData.customers;
        setCustName(c.custname || "");
        setMobile(c.mobile || "");
        setEmail(c.email || "");
        setAddress(c.address || "");
        setCity(c.city || "");
        setState(c.state || "");
        setGender(c.gender || "Male");
        setGstName(c.gstname || "");
        setGstNo(c.gstno || "");
        setDocumentType(c.documenttype || "");
        setIdNo(c.idno || "");
        setVisitPurpose(bookingData.visitpurpose || "Darshan");
        setBookingSource(bookingData.bookingsource || "Walking");
        setAdults(bookingData.adults || "");
        setChilds(bookingData.childs || "");
    };

    const updateCust = async () => {
        try {
            const customerData = {
                custname: custName,
                mobile,
                email,
                address,
                city,
                state,
                gender,
                gstname: gstName,
                gstno: gstNo,
                documenttype: documentType,
                idno: idNo,
                uid: userData?.uid,
                createdAt: Timestamp.now(),
            };

            await updateDoc(doc(db, "reservations", documentId), {
                customers: customerData,
                bookingsource: bookingSource,
                visitpurpose: visitPurpose,
                adults,
                childs,
            });

            if (bookingData.customerId) {
                await updateDoc(doc(db, "customers", bookingData.customerId), {
                    customers: customerData,
                    bookingsource: bookingSource,
                    visitpurpose: visitPurpose,
                });
            }

            Toast.fire({ icon: "success", title: 'Customer Details Successfully Updated' });
        } catch (error) {
            Toast.fire({ icon: "error", title: error.message });
        }
    };

    const handleMobileChange = (e) => {
        let value = e.target.value.replace(/\D/g, '');
        if (value.length <= 10) setMobile(value);
    };

    const handlePrint = () => {
        window.print();
    };

    if (!bookingData) return <Typography sx={{ p: 4 }}>Loading Invoice...</Typography>;

    return (
        <div>
            <Menu />
            <div className="content-wrapper">
                <section className="content mt-2">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-md-12">
                                <div className="print-area p-4 shadow-sm rounded">
                                    <CardContent >
                                        <Box  style={{borderWidth:1, borderColor:"#000"}} p={1} mb={2}>
                                            <Grid className="row" alignItems="center">
                                                <Grid className="col-sm-9" textAlign="left">
                                                    {propData.logo && (
                                                        <img src={propData.logo} alt="Logo" style={{ maxHeight: '60px', marginBottom: '8px' }} />
                                                    )}
                                                    <Typography variant="h5" fontWeight="bold" style={{ textTransform: "uppercase", color: "#d9534f" }}>
                                                        {propData.hotelname}
                                                    </Typography>
                                                    <Typography variant="subtitle2">
                                                        GSTIN No: {propData.gstno || propData.gstNo}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {propData.address}, {propData.city}, {propData.state} - {propData.pincode}
                                                        <br />
                                                        Phone: +91 {propData.mobile} | Email: {propData.email}
                                                    </Typography>
                                                </Grid>
                                                <Grid className="col-sm-3" textAlign="right">
                                                    <Typography variant="h5" fontWeight="bold">TAX INVOICE</Typography>
                                                </Grid>
                                            </Grid>
                                        </Box>

                                        <Divider sx={{ borderColor: "black", mb: 2 }} />

                                        {/* GUEST & INVOICE DETAILS GRID */}
                                        <Grid className="row" spacing={2}>
                                            <Grid item className="col-sm-6 mb-3">
                                                <Table size="small">
                                                    <TableBody>
                                                        <TableRow><TableCell><b>Guest Name</b></TableCell><TableCell>{bookingData.customers?.custname}</TableCell></TableRow>
                                                        <TableRow><TableCell><b>Address</b></TableCell><TableCell>{bookingData.customers?.address}, {bookingData.customers?.city}</TableCell></TableRow>
                                                        <TableRow><TableCell><b>Phone</b></TableCell><TableCell>{bookingData.customers?.mobile}</TableCell></TableRow>
                                                        <TableRow><TableCell><b>Email</b></TableCell><TableCell>{bookingData.customers?.email}</TableCell></TableRow>
                                                        <TableRow><TableCell><b>GST Name</b></TableCell><TableCell>{bookingData.customers?.gstname || "-"}</TableCell></TableRow>
                                                        <TableRow><TableCell><b>GST No</b></TableCell><TableCell>{bookingData.customers?.gstno || "-"}</TableCell></TableRow>
                                                    </TableBody>
                                                </Table>
                                            </Grid>
                                            <Grid item className="col-sm-6 mb-3">
                                                <Table size="small">
                                                    <TableBody>
                                                        <TableRow><TableCell><b>Invoice No</b></TableCell><TableCell>{bookingData.invoiceno}</TableCell></TableRow>
                                                        <TableRow><TableCell><b>Invoice Date</b></TableCell><TableCell>{moment().format("DD/MM/YYYY HH:mm:ss")}</TableCell></TableRow>
                                                        <TableRow><TableCell><b>Check-In</b></TableCell><TableCell>{moment(bookingData.checkindate).format("DD/MM/YYYY")} {bookingData.checkintime}</TableCell></TableRow>
                                                        <TableRow><TableCell><b>Check-Out</b></TableCell><TableCell>{moment(bookingData.checkoutdate).format("DD/MM/YYYY")} {bookingData.checkouttime}</TableCell></TableRow>
                                                        <TableRow><TableCell><b>No Of Persons</b></TableCell><TableCell>{bookingData.adults} Adults / {bookingData.childs} Children</TableCell></TableRow>
                                                    </TableBody>
                                                </Table>
                                            </Grid>
                                        </Grid>

                                        {/* ROOMS TABLE */}
                                        <TableContainer component={Paper} sx={{ mt: 3, boxShadow: 'none' }}>
                                            <Table size="medium">
                                                <TableHead sx={{ backgroundColor: '#f4f6f9' }}>
                                                    <TableRow>
                                                        <TableCell><b>Room Type</b></TableCell>
                                                        <TableCell><b>Room No</b></TableCell>
                                                        <TableCell><b>Plan</b></TableCell>
                                                        <TableCell align="right"><b>Rate</b></TableCell>
                                                        <TableCell align="right"><b>Days</b></TableCell>
                                                        <TableCell align="right"><b>Total Amount</b></TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {bookingData.rooms?.map((item, index) => {
                                                        const amount = parseFloat(item.amount || item.roomrate);
                                                        const total = amount * parseFloat(bookingData.noofdays || 1);
                                                        return (
                                                            <TableRow key={index}>
                                                                <TableCell>{item.roomtypename}</TableCell>
                                                                <TableCell>{item.roomno}</TableCell>
                                                                <TableCell>{item.planname || "Standard Plan"}</TableCell>
                                                                <TableCell align="right">{amount.toFixed(2)}</TableCell>
                                                                <TableCell align="right">{bookingData.noofdays}</TableCell>
                                                                <TableCell align="right">{total.toFixed(2)}</TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>

                                        {/* FINANCIAL TOTALS */}
                                        <Grid className="row" mt={3} justifyContent="flex-end">
                                            <Grid item className="col-sm-6 mb-3">
                                                <Table size="small">
                                                    <TableBody>
                                                        <TableRow><TableCell><b>Subtotal</b></TableCell><TableCell align="right">₹{subtotal}</TableCell></TableRow>
                                                        <TableRow><TableCell>CGST</TableCell><TableCell align="right">₹{cgst}</TableCell></TableRow>
                                                        <TableRow><TableCell>SGST</TableCell><TableCell align="right">₹{sgst}</TableCell></TableRow>
                                                        <TableRow><TableCell><b>Grand Total</b></TableCell><TableCell align="right"><b>₹{bookingData.totalamount}</b></TableCell></TableRow>
                                                        <TableRow><TableCell>Paid Amount</TableCell><TableCell align="right">₹{bookingData.advance || 0}</TableCell></TableRow>
                                                        <TableRow><TableCell><b>Balance Due</b></TableCell><TableCell align="right"><b>₹{bookingData.remaining || 0}</b></TableCell></TableRow>
                                                    </TableBody>
                                                </Table>
                                            </Grid>
                                        </Grid>

                                        {/* AMOUNT IN WORDS */}
                                        <Box mt={2} py={1.5} px={2} sx={{ borderTop: "1px solid #ddd", borderBottom: "1px solid #ddd" }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                <span style={{ color: "#E65100" }}>Amount in Words:</span>{" "}
                                                <span style={{ fontStyle: "italic", textTransform: "capitalize" }}>
                                                    {amountInWords} Rupees Only
                                                </span>
                                            </Typography>
                                        </Box>

                                        {/* PAYMENT BREAKUP TABLE */}
                                        {payHistory.length > 0 && (
                                            <Box mt={3}>
                                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Payment Breakup</Typography>
                                                <Table size="small" bordered>
                                                    <TableHead sx={{ backgroundColor: '#f9f9f9' }}>
                                                        <TableRow>
                                                            <TableCell><b>Date</b></TableCell>
                                                            <TableCell><b>Mode</b></TableCell>
                                                            <TableCell align="right"><b>Amount</b></TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {payHistory.map((pay, i) => (
                                                            <TableRow key={i}>
                                                                <TableCell>{pay.createdat}</TableCell>
                                                                <TableCell>{pay.paymode1 || "Cash/UPI"}</TableCell>
                                                                <TableCell align="right">₹{pay.data.amount || bookingData.advance}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </Box>
                                        )}

                                        {/* SIGNATURE SECTION */}
                                        <Grid className="row" mt={8} alignItems="center">
                                            <Grid item className="col-sm-8 mb-3" textAlign="left">
                                                <Typography variant="caption">Guest Signature</Typography>
                                            </Grid>
                                            <Grid item className="col-sm-4 mb-3" textAlign="right">
                                                {propData.stamp && (
                                                    <img src={propData.stamp} alt="Stamp" style={{ maxHeight: '70px', objectFit: 'contain' , marginBottom:5}} />
                                                )}
                                                <Typography variant="subtitle2" fontWeight="bold">{propData.hotelname}</Typography>
                                                <Typography variant="caption">Authorized Signatory</Typography>
                                            </Grid>

                                        </Grid>
                                    </CardContent>
                                </div>

                                <Box textAlign="center" mt={3}>
                                    <Button variant="contained" color="success" onClick={handlePrint}>
                                        Print Invoice
                                    </Button>
                                </Box>
                            </div>

                            {/* SIDEBAR PANEL */}
                            <div className="col-md-12 mt-5">
                                <div className="card card-primary card-outline">
                                    <div className="card-body box-profile">
                                        <h3 className="profile-username text-center">{bookingData.customers?.custname}</h3>
                                        <p className="text-muted text-center">{bookingData.customers?.mobile}</p>
                                        <div className="text-center mb-3">
                                            <Button size="small" variant="outlined" onClick={getCustData} data-toggle="modal" data-target="#custModal">
                                                Edit Details
                                            </Button>
                                        </div>
                                        <ul className="list-group list-group-unbordered mb-3">
                                            <li className="list-group-item"><b>Email</b> <span className="float-right">{bookingData.customers?.email}</span></li>
                                            <li className="list-group-item"><b>City</b> <span className="float-right">{bookingData.customers?.city}</span></li>
                                            <li className="list-group-item"><b>Gender</b> <span className="float-right">{bookingData.customers?.gender}</span></li>
                                            <li className="list-group-item"><b>Source</b> <span className="float-right">{bookingData.bookingsource}</span></li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* EDIT CUSTOMER MODAL */}
                <div className="modal fade" id="custModal" tabIndex={-1} role="dialog" aria-hidden="true">
                    <div className="modal-dialog modal-xl" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Update Customer Details</h5>
                                <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                                    <span aria-hidden="true">×</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                <Grid className="row" spacing={2}>
                                    <Grid className="col-sm-3 mb-3" item xs={3}><TextField fullWidth label="Customer Name" size="small" value={custName} onChange={(e) => setCustName(e.target.value)} /></Grid>
                                    <Grid className="col-sm-3 mb-3" item xs={3}><TextField fullWidth label="Mobile" size="small" value={mobile} onChange={handleMobileChange} /></Grid>
                                    <Grid className="col-sm-3 mb-3" item xs={3}><TextField fullWidth label="Email" size="small" value={email} onChange={(e) => setEmail(e.target.value)} /></Grid>
                                    <Grid className="col-sm-3 mb-3" item xs={3}><TextField fullWidth label="Address" size="small" value={address} onChange={(e) => setAddress(e.target.value)} /></Grid>
                                    <Grid className="col-sm-3 mb-3" item xs={3}><TextField fullWidth label="City" size="small" value={city} onChange={(e) => setCity(e.target.value)} /></Grid>
                                    <Grid className="col-sm-3 mb-3" item xs={3}><TextField fullWidth label="State" size="small" value={state} onChange={(e) => setState(e.target.value)} /></Grid>
                                    <Grid className="col-sm-3 mb-3" item xs={3}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Gender</InputLabel>
                                            <Select value={gender} label="Gender" onChange={(e) => setGender(e.target.value)}>
                                                <MenuItem value='Male'>Male</MenuItem>
                                                <MenuItem value='Female'>Female</MenuItem>
                                                <MenuItem value='Other'>Other</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid className="col-sm-3 mb-3" item xs={3}><TextField fullWidth label="GST Name" size="small" value={gstName} onChange={(e) => setGstName(e.target.value)} /></Grid>
                                    <Grid className="col-sm-3 mb-3" item xs={3}><TextField fullWidth label="GST No" size="small" value={gstNo} onChange={(e) => setGstNo(e.target.value)} /></Grid>
                                </Grid>
                            </div>
                            <div className="modal-footer">
                                <Button variant="contained" color="success" onClick={updateCust} data-dismiss="modal">Save Changes</Button>
                                <Button variant="secondary" data-dismiss="modal">Close</Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Hotelinvoice;